/**
 * MessagesPage - Direct messaging between friends
 * Features:
 * - Conversation list with unread counts
 * - Real-time messaging
 * - Media sharing from discover pages
 * - Joint list invitations
 * - Clickable media cards to view details and add to lists
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  MessageSquare, Send, ArrowLeft, Loader2, Users, Check, X,
  Tv, Film, Mic, BookOpen, ListPlus, ExternalLink, Plus, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import { format, formatDistanceToNow } from 'date-fns';
import { SeriesDetailsDialog } from '../components/SeriesDetailsDialog';
import { getTMDBDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Media type icons
const MEDIA_ICONS = {
  tv: Tv,
  movie: Film,
  podcast: Mic,
  book: BookOpen,
};

function ConversationList({ conversations, selectedUserId, onSelect, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#F87171]" />
      </div>
    );
  }
  
  if (conversations.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="w-12 h-12 bg-[#F87171]/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <MessageSquare className="w-6 h-6 text-[#F87171]" />
        </div>
        <h3 className="font-medium text-[#3D4F6F] mb-1">No messages yet</h3>
        <p className="text-sm text-[#64748B]">
          Start a conversation with a friend!
        </p>
      </div>
    );
  }
  
  return (
    <div className="divide-y divide-slate-100">
      {conversations.map((conv) => (
        <button
          key={conv.user_id}
          onClick={() => onSelect(conv.user_id)}
          className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left ${
            selectedUserId === conv.user_id ? 'bg-[#F87171]/5' : ''
          }`}
          data-testid={`conversation-${conv.user_id}`}
        >
          <Avatar className="h-12 w-12 flex-shrink-0">
            {conv.user_avatar ? (
              <AvatarImage src={conv.user_avatar} />
            ) : (
              <AvatarFallback className="bg-[#F87171]/10 text-[#F87171]">
                {conv.user_name?.charAt(0) || '?'}
              </AvatarFallback>
            )}
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-[#3D4F6F] truncate">{conv.user_name}</span>
              {conv.last_message?.created_at && (
                <span className="text-xs text-[#64748B] flex-shrink-0">
                  {formatDistanceToNow(new Date(conv.last_message.created_at), { addSuffix: true })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-[#64748B] truncate flex-1">
                {conv.last_message?.is_from_me && <span className="text-[#94A3B8]">You: </span>}
                {conv.last_message?.message_type === 'media_share' 
                  ? '📺 Shared media'
                  : conv.last_message?.message_type === 'list_invite'
                    ? '📋 List invitation'
                    : conv.last_message?.content || 'No messages'
                }
              </p>
              {conv.unread_count > 0 && (
                <Badge className="bg-[#F87171] text-white text-xs px-2 py-0.5">
                  {conv.unread_count}
                </Badge>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function MessageBubble({ message, isFromMe, onRespondToInvite, onMediaClick }) {
  const MediaIcon = message.media_data ? MEDIA_ICONS[message.media_data.media_type] : null;
  
  // Format the time
  const messageTime = message.created_at 
    ? format(new Date(message.created_at), 'h:mm a')
    : '';
  
  // Handle list invite
  if (message.message_type === 'list_invite') {
    const listData = message.list_data || {};
    const isPending = listData.status === 'pending';
    
    return (
      <div className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[80%] rounded-2xl p-3 ${
          isFromMe ? 'bg-[#F87171] text-white' : 'bg-white border border-slate-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <ListPlus className="w-4 h-4" />
            <span className="font-medium text-sm">List Invitation</span>
          </div>
          <p className="text-sm mb-2">{message.content}</p>
          <div className={`p-2 rounded-lg ${isFromMe ? 'bg-white/20' : 'bg-slate-50'}`}>
            <p className={`font-medium ${isFromMe ? 'text-white' : 'text-[#3D4F6F]'}`}>
              {listData.list_name}
            </p>
            <p className={`text-xs ${isFromMe ? 'text-white/80' : 'text-[#64748B]'}`}>
              {listData.media_category} list
            </p>
          </div>
          
          {!isFromMe && isPending && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={() => onRespondToInvite(message.id, true)}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs"
              >
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRespondToInvite(message.id, false)}
                className="flex-1 rounded-full text-xs"
              >
                <X className="w-3 h-3 mr-1" /> Decline
              </Button>
            </div>
          )}
          
          {listData.status === 'accepted' && (
            <Badge className="mt-2 bg-green-100 text-green-700">Accepted</Badge>
          )}
          {listData.status === 'declined' && (
            <Badge className="mt-2 bg-slate-100 text-slate-600">Declined</Badge>
          )}
          
          <p className={`text-xs mt-2 ${isFromMe ? 'text-white/60' : 'text-[#94A3B8]'}`}>
            {messageTime}
          </p>
        </div>
      </div>
    );
  }
  
  // Handle media share
  if (message.message_type === 'media_share' && message.media_data) {
    const media = message.media_data;
    
    return (
      <div className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[80%] rounded-2xl p-3 ${
          isFromMe ? 'bg-[#F87171] text-white' : 'bg-white border border-slate-200'
        }`}>
          {message.content && (
            <p className="text-sm mb-2">{message.content}</p>
          )}
          {/* Clickable media card */}
          <button
            onClick={() => onMediaClick?.(media)}
            className={`w-full flex gap-3 p-2 rounded-lg transition-all ${
              isFromMe 
                ? 'bg-white/20 hover:bg-white/30' 
                : 'bg-slate-50 hover:bg-slate-100'
            }`}
            data-testid={`media-card-${media.item_id}`}
          >
            {media.poster && (
              <img 
                src={media.poster} 
                alt={media.title}
                className="w-16 h-24 object-cover rounded flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-1 mb-1">
                {MediaIcon && <MediaIcon className="w-3 h-3" />}
                <span className={`text-xs ${isFromMe ? 'text-white/80' : 'text-[#64748B]'}`}>
                  {media.media_type === 'tv' ? 'TV Show' : 
                   media.media_type === 'movie' ? 'Movie' :
                   media.media_type === 'podcast' ? 'Podcast' : 'Book'}
                </span>
              </div>
              <p className={`font-medium text-sm ${isFromMe ? 'text-white' : 'text-[#3D4F6F]'}`}>
                {media.title}
              </p>
              {media.year && (
                <p className={`text-xs ${isFromMe ? 'text-white/70' : 'text-[#64748B]'}`}>
                  {media.year}
                </p>
              )}
              <div className={`flex items-center gap-1 mt-2 text-xs ${isFromMe ? 'text-white/80' : 'text-[#F87171]'}`}>
                <Info className="w-3 h-3" />
                <span>Tap to view details</span>
              </div>
            </div>
          </button>
          <p className={`text-xs mt-2 ${isFromMe ? 'text-white/60' : 'text-[#94A3B8]'}`}>
            {messageTime}
          </p>
        </div>
      </div>
    );
  }
  
  // Regular text message
  return (
    <div className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
        isFromMe 
          ? 'bg-[#F87171] text-white rounded-br-md' 
          : 'bg-white border border-slate-200 rounded-bl-md'
      }`}>
        <p className="text-sm">{message.content}</p>
        <p className={`text-xs mt-1 ${isFromMe ? 'text-white/60' : 'text-[#94A3B8]'}`}>
          {messageTime}
        </p>
      </div>
    </div>
  );
}

function ChatView({ otherUserId, onBack, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  
  // Media details dialog state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaDetails, setMediaDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${API}/messages/conversation/${otherUserId}`,
        { withCredentials: true, headers }
      );
      
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [otherUserId]);
  
  useEffect(() => {
    fetchMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Handle media card click - fetch details and show dialog
  const handleMediaClick = async (media) => {
    setSelectedMedia(media);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Handle different media types
      if (media.media_type === 'tv' || media.media_type === 'movie') {
        // Use getTMDBDetails for TV/Movies
        const details = await getTMDBDetails(media.item_id, media.media_type);
        setMediaDetails(details);
      } else if (media.media_type === 'podcast') {
        // Fetch podcast details
        const response = await axios.get(
          `${API}/podcasts/${media.item_id}`,
          { withCredentials: true, headers }
        );
        setMediaDetails({
          ...response.data,
          media_type: 'podcast',
          title: response.data.title,
          poster_path: response.data.thumbnail || response.data.image,
          overview: response.data.description,
        });
      } else if (media.media_type === 'book') {
        // Fetch book details
        const response = await axios.get(
          `${API}/books/${media.item_id}`,
          { withCredentials: true, headers }
        );
        setMediaDetails({
          ...response.data,
          media_type: 'book',
          title: response.data.title,
          poster_path: response.data.cover_url || response.data.thumbnail,
          overview: response.data.description,
          genres: response.data.genres || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch media details:', error);
      // Fallback to basic info from the message
      setMediaDetails({
        ...media,
        title: media.title,
        poster_path: media.poster,
        media_type: media.media_type,
      });
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Handle add to list from details dialog
  const handleAddToList = async (item) => {
    setShowAddToListDialog(true);
    setLoadingLists(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let endpoint = '/watchlists';
      if (selectedMedia?.media_type === 'podcast') {
        endpoint = '/podcast-lists';
      } else if (selectedMedia?.media_type === 'book') {
        endpoint = '/book-lists';
      } else if (selectedMedia?.media_type === 'movie') {
        endpoint = '/watchlists?media_category=movie';
      } else if (selectedMedia?.media_type === 'tv') {
        endpoint = '/watchlists?media_category=tv';
      }
      
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true, headers });
      setWatchlists(response.data || []);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
      toast.error('Failed to load lists');
    } finally {
      setLoadingLists(false);
    }
  };
  
  // Add item to selected list
  const handleAddToSelectedList = async (listId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let endpoint = '';
      let payload = {};
      
      if (selectedMedia?.media_type === 'podcast') {
        endpoint = `/podcast-lists/${listId}/podcasts`;
        payload = {
          podcast_id: selectedMedia.item_id,
          title: mediaDetails?.title || selectedMedia.title,
          publisher: mediaDetails?.publisher,
          thumbnail: mediaDetails?.poster_path || selectedMedia.poster,
          position: 'bottom'
        };
      } else if (selectedMedia?.media_type === 'book') {
        endpoint = `/book-lists/${listId}/books`;
        payload = {
          book_id: selectedMedia.item_id,
          title: mediaDetails?.title || selectedMedia.title,
          authors: mediaDetails?.authors || [],
          cover_url: mediaDetails?.poster_path || selectedMedia.poster,
          position: 'bottom'
        };
      } else {
        // TV or Movie
        endpoint = `/watchlists/${listId}/series`;
        payload = {
          tmdb_id: parseInt(selectedMedia.item_id),
          title: mediaDetails?.title || selectedMedia.title,
          poster_path: mediaDetails?.poster_path || selectedMedia.poster,
          media_type: selectedMedia.media_type,
          position: 'bottom'
        };
      }
      
      await axios.post(`${API}${endpoint}`, payload, { withCredentials: true, headers });
      toast.success(`Added to list!`);
      setShowAddToListDialog(false);
      setSelectedMedia(null);
      setMediaDetails(null);
    } catch (error) {
      console.error('Failed to add to list:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    }
  };
  
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API}/messages`,
        {
          recipient_id: otherUserId,
          content: newMessage.trim(),
          message_type: 'text'
        },
        { withCredentials: true, headers }
      );
      
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  const handleRespondToInvite = async (messageId, accept) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API}/messages/respond-to-invite/${messageId}?accept=${accept}`,
        {},
        { withCredentials: true, headers }
      );
      
      toast.success(accept ? 'Invitation accepted!' : 'Invitation declined');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to respond to invitation');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 bg-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="md:hidden rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <Avatar className="h-10 w-10">
          {otherUser?.avatar_url ? (
            <AvatarImage src={otherUser.avatar_url} />
          ) : (
            <AvatarFallback className="bg-[#F87171]/10 text-[#F87171]">
              {otherUser?.name?.charAt(0) || '?'}
            </AvatarFallback>
          )}
        </Avatar>
        
        <div className="flex-1">
          <h3 className="font-medium text-[#3D4F6F]">{otherUser?.name || 'Unknown'}</h3>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/profile/${otherUserId}`)}
          className="rounded-full text-xs"
        >
          View Profile
        </Button>
      </div>
      
      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#64748B]">No messages yet. Say hello!</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isFromMe={msg.is_from_me}
                onRespondToInvite={handleRespondToInvite}
                onMediaClick={handleMediaClick}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </ScrollArea>
      
      {/* Message Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-slate-200 bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 rounded-full bg-slate-50"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="rounded-full bg-[#F87171] hover:bg-[#EF4444]"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
      
      {/* Media Details Dialog for TV/Movies */}
      {selectedMedia && (selectedMedia.media_type === 'tv' || selectedMedia.media_type === 'movie') && (
        <SeriesDetailsDialog
          open={!!selectedMedia}
          onClose={() => {
            setSelectedMedia(null);
            setMediaDetails(null);
          }}
          loading={loadingDetails}
          details={mediaDetails}
          onAddToList={handleAddToList}
        />
      )}
      
      {/* Media Details Dialog for Podcasts/Books */}
      {selectedMedia && (selectedMedia.media_type === 'podcast' || selectedMedia.media_type === 'book') && (
        <Dialog open={!!selectedMedia} onOpenChange={() => {
          setSelectedMedia(null);
          setMediaDetails(null);
        }}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
              </div>
            ) : mediaDetails && (
              <>
                <div className="flex gap-4">
                  {mediaDetails.poster_path && (
                    <img 
                      src={mediaDetails.poster_path} 
                      alt={mediaDetails.title}
                      className="w-24 h-36 object-cover rounded-lg shadow"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {selectedMedia.media_type === 'podcast' ? (
                        <Mic className="w-4 h-4 text-[#F87171]" />
                      ) : (
                        <BookOpen className="w-4 h-4 text-[#F87171]" />
                      )}
                      <span className="text-xs text-[#64748B]">
                        {selectedMedia.media_type === 'podcast' ? 'Podcast' : 'Book'}
                      </span>
                    </div>
                    <h2 className="font-outfit text-lg font-bold text-[#3D4F6F] mb-2">
                      {mediaDetails.title}
                    </h2>
                    {mediaDetails.authors && mediaDetails.authors.length > 0 && (
                      <p className="text-sm text-[#64748B] mb-2">
                        by {mediaDetails.authors.join(', ')}
                      </p>
                    )}
                    {mediaDetails.publisher && (
                      <p className="text-sm text-[#64748B] mb-2">
                        {mediaDetails.publisher}
                      </p>
                    )}
                    {mediaDetails.genres && mediaDetails.genres.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {mediaDetails.genres.slice(0, 3).map((genre, i) => (
                          <Badge key={i} variant="secondary" className="bg-[#F87171]/10 text-[#F87171] text-xs">
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {mediaDetails.overview && (
                  <div className="mt-4">
                    <h3 className="font-medium text-[#3D4F6F] mb-2">Description</h3>
                    <p className="text-sm text-[#64748B] line-clamp-4">{mediaDetails.overview}</p>
                  </div>
                )}
                
                <Button
                  onClick={() => handleAddToList(mediaDetails)}
                  className="w-full mt-4 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to List
                </Button>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
      
      {/* Add to List Selection Dialog */}
      <Dialog open={showAddToListDialog} onOpenChange={setShowAddToListDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#3D4F6F]">Add to List</DialogTitle>
            <DialogDescription>
              Choose a list for &ldquo;{mediaDetails?.title || selectedMedia?.title}&rdquo;
            </DialogDescription>
          </DialogHeader>
          
          {loadingLists ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#F87171]" />
            </div>
          ) : watchlists.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-[#64748B] mb-3">No lists yet</p>
              <Button
                onClick={() => {
                  setShowAddToListDialog(false);
                  // Navigate to appropriate page to create list
                  if (selectedMedia?.media_type === 'tv') navigate('/tv');
                  else if (selectedMedia?.media_type === 'movie') navigate('/films');
                  else if (selectedMedia?.media_type === 'podcast') navigate('/podcasts');
                  else if (selectedMedia?.media_type === 'book') navigate('/books');
                }}
                variant="outline"
                className="rounded-full"
              >
                Create a List
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {watchlists.map((list) => (
                <button
                  key={list.id}
                  onClick={() => handleAddToSelectedList(list.id)}
                  className="w-full p-3 flex items-center gap-3 rounded-lg hover:bg-slate-50 transition-colors text-left border border-slate-200"
                >
                  <div className="w-10 h-10 bg-[#F87171]/10 rounded-lg flex items-center justify-center">
                    <ListPlus className="w-5 h-5 text-[#F87171]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#3D4F6F]">{list.name}</p>
                    <p className="text-xs text-[#64748B]">
                      {list.series?.length || list.podcasts?.length || list.books?.length || 0} items
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MessagesPage() {
  const { userId: urlUserId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(urlUserId || null);
  const [friends, setFriends] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const navigate = useNavigate();
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        setUser(response.data);
      } catch (error) {
        navigate('/');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/messages/conversations`, { 
        withCredentials: true, 
        headers 
      });
      
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Fetch friends list
  const fetchFriends = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/messages/friends`, { 
        withCredentials: true, 
        headers 
      });
      
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    }
  }, [user]);
  
  useEffect(() => {
    fetchConversations();
    fetchFriends();
    
    // Poll for new conversations every 10 seconds
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchFriends]);
  
  // Update URL when selecting conversation
  useEffect(() => {
    if (selectedUserId && selectedUserId !== urlUserId) {
      navigate(`/messages/${selectedUserId}`, { replace: true });
    }
  }, [selectedUserId, urlUserId, navigate]);
  
  const handleSelectConversation = (userId) => {
    setSelectedUserId(userId);
    setShowNewChat(false);
  };
  
  const handleStartNewChat = (friendId) => {
    setSelectedUserId(friendId);
    setShowNewChat(false);
  };
  
  if (!user) return null;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2FF]">
      <AppHeader user={user} />
      
      <main className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F]">Messages</h1>
            <p className="text-sm text-[#64748B]">Chat with your friends</p>
          </div>
          <Button
            onClick={() => setShowNewChat(true)}
            className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>
        
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="flex h-[calc(100vh-220px)] min-h-[500px]">
            {/* Conversation List - Hidden on mobile when chat selected */}
            <div className={`w-full md:w-80 border-r border-slate-200 bg-white ${
              selectedUserId ? 'hidden md:block' : 'block'
            }`}>
              {/* New Chat Mode */}
              {showNewChat ? (
                <div>
                  <div className="p-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-[#3D4F6F]">Start New Chat</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewChat(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {friends.length === 0 ? (
                      <div className="text-center py-8 px-4">
                        <Users className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                        <p className="text-sm text-[#64748B]">
                          No friends yet. Follow someone and get followed back to start chatting!
                        </p>
                      </div>
                    ) : (
                      friends.map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => handleStartNewChat(friend.user_id)}
                          className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
                        >
                          <Avatar className="h-10 w-10">
                            {friend.avatar_url ? (
                              <AvatarImage src={friend.avatar_url} />
                            ) : (
                              <AvatarFallback className="bg-[#F87171]/10 text-[#F87171]">
                                {friend.name?.charAt(0) || '?'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <span className="font-medium text-[#3D4F6F]">{friend.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <ConversationList
                  conversations={conversations}
                  selectedUserId={selectedUserId}
                  onSelect={handleSelectConversation}
                  loading={loading}
                />
              )}
            </div>
            
            {/* Chat View */}
            <div className={`flex-1 bg-[#F8FAFC] ${
              !selectedUserId ? 'hidden md:flex' : 'flex'
            } flex-col`}>
              {selectedUserId ? (
                <ChatView
                  otherUserId={selectedUserId}
                  onBack={() => setSelectedUserId(null)}
                  currentUser={user}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#F87171]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-8 h-8 text-[#F87171]" />
                    </div>
                    <h3 className="font-medium text-[#3D4F6F] mb-2">Select a conversation</h3>
                    <p className="text-sm text-[#64748B]">
                      Choose a friend to start chatting
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
